import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import apiReports, { IOfferLogEntry } from 'api/reports';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SelectInput from 'components/layout/forms/SelectInput';
import TextInputField from 'components/layout/forms/TextInputField';
import OfferLogTable from 'components/offers/src/offer/OfferLogTable';
import translations from 'services/translations/translations';

const PAGE_SIZE = 100;

// The periods to show, in days; 0 = everything
const PERIODS = [1, 7, 30, 90, 0];

/**
 * The log of all offers: who opened, calculated and saved them, newest first. Only for the
 * users with the right to read the log.
 */
const OfferLog = (): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const [entries, setEntries] = useState<IOfferLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  // The search is made when the user stops typing
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setAppliedSearch(search), 400);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const load = async (offset: number) => {
    setLoading(true);
    setFailed(false);
    try {
      const page = await apiReports.getOfferLog({
        limit: PAGE_SIZE,
        offset,
        search: appliedSearch.trim() === '' ? undefined : appliedSearch.trim(),
        since:
          days === 0
            ? undefined
            : new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(),
      });
      setEntries(offset === 0 ? page.entries : [...entries, ...page.entries]);
      setTotal(page.total);
    } catch (error) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
  }, [days, appliedSearch]);

  return (
    <PageContainer>
      <p className="header-text">{t(`${translations.offers}:history.Log`)}</p>
      <p className="offer-history-info">
        {t(`${translations.offers}:history.Log info`)}
      </p>
      <Row>
        <Col md={3}>
          <SelectInput
            id="offer-log-period"
            key="offer-log-period"
            label={t(`${translations.offers}:history.Period`)}
            onChange={(event) => setDays(parseInt(event.target.value, 10))}
            options={PERIODS.map((period) => ({
              displayValue: t(
                `${translations.offers}:history.periods.${period}`
              ),
              value: period.toString(),
            }))}
            value={days.toString()}
          />
        </Col>
        <Col md={5}>
          <TextInputField
            id="offer-log-search"
            label={t(`${translations.offers}:history.Search`)}
            name="search"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(event.target.value)
            }
            value={search}
          />
        </Col>
      </Row>
      {failed ? <p>{t(`${translations.offers}:history.Failed`)}</p> : null}
      {!failed && !loading && entries.length === 0 ? (
        <p>{t(`${translations.offers}:history.Empty`)}</p>
      ) : null}
      {entries.length > 0 ? (
        <OfferLogTable entries={entries} withOffer />
      ) : null}
      {entries.length < total ? (
        <PrimaryButton disabled={loading} onClick={() => load(entries.length)}>
          {t(`${translations.offers}:history.More`, {
            count: total - entries.length,
          }).toLocaleUpperCase()}
        </PrimaryButton>
      ) : null}
    </PageContainer>
  );
};

export default OfferLog;
